import { defineStore } from 'pinia';
import axios from 'axios';

class LineItem {
  constructor(slug, title, amountCents, coverUrl) {
    return {
      quantity: 1,
      price_data: {
        currency: 'usd',
        unit_amount: amountCents,
        product_data: {
          name: title,
          images: coverUrl ? [coverUrl] : [],
        },
      },
      // internal reference (not sent to Stripe)
      _slug: slug,
    };
  }
}

export const useCartStore = defineStore('cart', {
  state: () => ({
    api: '/api/cart/checkout',
    line_items: [],
    isOpen: false,
    orderType: 'individual',
    shippingRates: [],        // [{id, name, amount, currency}] from Stripe
    selectedRateId: null,     // shr_xxx
    session: {},
    sessionError: false,
  }),

  getters: {
    itemsTotal: (state) => {
      return state.line_items.reduce((sum, item) => {
        return sum + (item.price_data.unit_amount / 100) * item.quantity;
      }, 0);
    },
    shippingTotal: (state) => {
      const rate = state.shippingRates.find(r => r.id === state.selectedRateId);
      return rate ? rate.amount / 100 : 0;
    },
    subtotal() {
      return this.itemsTotal + this.shippingTotal;
    },
    itemCount: (state) => {
      return state.line_items.reduce((sum, item) => sum + item.quantity, 0);
    },
  },

  actions: {
    async fetchShippingRates() {
      if (this.shippingRates.length) return; // already loaded
      try {
        const res = await axios.get('/api/cart/shipping-rates');
        this.shippingRates = res.data.rates ?? [];
        if (this.shippingRates.length && !this.selectedRateId) {
          this.selectedRateId = this.shippingRates[0].id;
        }
      } catch (e) {
        console.error('Failed to load shipping rates:', e);
      }
    },

    async addItem(book) {
      const amountCents = Math.round(parseFloat(book.price) * 100);
      const existing = this.line_items.find(i => i._slug === book.uid);
      if (existing) {
        existing.quantity++;
      } else {
        this.line_items.push(
          new LineItem(book.uid, book.title, amountCents, book.coverimages?.[0]?.url ?? null)
        );
      }
      this.isOpen = true;
      await this.fetchShippingRates();
      this.loadSession();
    },

    removeItem(slug) {
      this.line_items = this.line_items.filter(i => i._slug !== slug);
      this.loadSession();
    },

    updateQuantity(slug, qty) {
      const item = this.line_items.find(i => i._slug === slug);
      if (item) {
        item.quantity = Math.max(1, qty);
        this.loadSession();
      }
    },

    async loadSession() {
      if (!this.line_items.length) return;

      // Strip internal _slug and rewrite image URLs to public domain for Stripe
      const stripeItems = this.line_items.map(({ _slug, ...item }) => {
        const images = (item.price_data.product_data.images ?? []).map(url => {
          try {
            const u = new URL(url);
            u.host = 'publictrust.org';
            u.protocol = 'https:';
            u.port = '';
            return u.toString();
          } catch { return url; }
        });
        return {
          ...item,
          price_data: {
            ...item.price_data,
            product_data: { ...item.price_data.product_data, images },
          },
        };
      });

      const selectedRate = this.shippingRates.find(r => r.id === this.selectedRateId);
      const payload = {
        line_items: stripeItems,
        mode: 'payment',
        shipping_rate_id: this.selectedRateId ?? null,
        metadata: {
          order_type: this.orderType,
          shipping_rate: selectedRate?.name ?? '',
        },
        success_url: window.location.origin + '/order-confirmation',
        cancel_url: window.location.href,
      };

      try {
        this.sessionError = false;
        const response = await axios.post(this.api, payload);
        this.session = response.data.session;
      } catch (e) {
        console.error('Checkout session error:', e);
        this.sessionError = true;
      }
    },

    goToCheckout() {
      if (this.session?.url) {
        window.open(this.session.url);
      } else {
        // Fallback: mailto for manual orders
        const items = this.line_items.map(i =>
          `${i.price_data.product_data.name} ×${i.quantity}`
        ).join(', ');
        window.location.href = `mailto:shop@publictrust.org?subject=Book Order&body=I'd like to order: ${encodeURIComponent(items)}`;
      }
    },

    open() { this.isOpen = true; this.fetchShippingRates(); },
    close() { this.isOpen = false; },
  },
});
